/**
 * Las condiciones que valen para todo el pais (ADM-10): si se ofrece despacho a
 * domicilio y desde que monto es gratis, y si se ofrece retiro en la sala de
 * ventas y con que plazo.
 *
 * Son dos tarjetas iguales, una por forma de entrega, y cada una guarda lo
 * suyo. El interruptor se guarda al tocarlo, sin boton aparte: es un si/no que
 * se revierte con otro toque, y un interruptor que muestra "Activo" sin estarlo
 * en la tienda enganaria. El endpoint recibe todo junto, asi que cada guardado
 * completa lo que no toca con lo ultimo guardado.
 *
 * Con las dos apagadas no se deja de vender: el comprador paga solo los
 * productos y la entrega se coordina por telefono (`entregasDisponibles()` en
 * el backend). Eso se avisa debajo, porque no es obvio desde los interruptores.
 */
import { useState } from 'react';
import type { FormEvent } from 'react';

import type { ActualizarConfiguracionDespachoDto, DespachoAdmin } from '@bandu/compartido';

import { SITIO } from '../../configuracion/sitio';
import { guardarConfiguracionDespacho } from '../../servicios/despacho-admin.servicio';
import { pesos } from '../../utilidades/formato';
import { mensajeDe } from '../../utilidades/mensaje-error';
import { Icono } from '../comunes/Icono';
import { InterruptorGrande } from './InterruptorGrande';

interface CondicionesDespachoProps {
  datos: DespachoAdmin;
  alGuardar: (datos: DespachoAdmin) => void;
  alFallar: (mensaje: string | null) => void;
}

type Tarjeta = 'despacho' | 'retiro';

export function CondicionesDespacho({ datos, alGuardar, alFallar }: CondicionesDespachoProps) {
  const [gratisDesde, setGratisDesde] = useState(String(datos.despachoGratisDesde));
  const [plazoRetiro, setPlazoRetiro] = useState(datos.plazoRetiro);
  const [guardando, setGuardando] = useState<Tarjeta | null>(null);
  const [guardada, setGuardada] = useState<Tarjeta | null>(null);

  const gratisCambio = Number(gratisDesde) !== datos.despachoGratisDesde;
  const plazoCambio = plazoRetiro !== datos.plazoRetiro;
  const { despachoHabilitado, retiroHabilitado } = datos;

  async function guardar(
    tarjeta: Tarjeta,
    cambio: Partial<ActualizarConfiguracionDespachoDto>,
  ): Promise<void> {
    setGuardando(tarjeta);
    setGuardada(null);
    alFallar(null);

    try {
      alGuardar(
        await guardarConfiguracionDespacho({
          despachoGratisDesde: datos.despachoGratisDesde,
          despachoHabilitado: datos.despachoHabilitado,
          retiroHabilitado: datos.retiroHabilitado,
          plazoRetiro: datos.plazoRetiro,
          ...cambio,
        }),
      );
      setGuardada(tarjeta);
    } catch (fallo) {
      alFallar(mensajeDe(fallo));
    } finally {
      setGuardando(null);
    }
  }

  function alEnviar(tarjeta: Tarjeta, cambio: Partial<ActualizarConfiguracionDespachoDto>) {
    return (evento: FormEvent<HTMLFormElement>) => {
      evento.preventDefault();
      void guardar(tarjeta, cambio);
    };
  }

  return (
    <>
      <div className="condiciones-despacho">
        <form
          className={despachoHabilitado ? 'tarjeta-condicion' : 'tarjeta-condicion apagada'}
          onSubmit={alEnviar('despacho', { despachoGratisDesde: Number(gratisDesde) })}
        >
          <h2 className="tarjeta-condicion-titulo">
            <Icono nombre="camion" />
            Despacho a domicilio
          </h2>
          <InterruptorGrande
            encendido={despachoHabilitado}
            titulo={despachoHabilitado ? 'Se ofrece despacho' : 'No se ofrece despacho'}
            detalleEncendido="El cliente puede pedir que se lo lleven, con la tarifa de su comuna."
            detalleApagado={
              retiroHabilitado
                ? 'El checkout solo ofrece retiro, y la tienda no anuncia despacho gratis.'
                : 'La tienda no anuncia despacho gratis.'
            }
            deshabilitado={guardando === 'despacho'}
            alCambiar={(encendido) => {
              void guardar('despacho', { despachoHabilitado: encendido });
            }}
          />
          <label htmlFor="cfg-gratis" className="tarjeta-condicion-etiqueta">
            Gratis para compras desde
          </label>
          <div className="entrada-pesos grande">
            <span aria-hidden="true">$</span>
            <input
              id="cfg-gratis"
              className={gratisCambio ? 'entrada cambiado' : 'entrada'}
              type="number"
              inputMode="numeric"
              min={1}
              step={1}
              value={gratisDesde}
              disabled={!despachoHabilitado}
              onChange={(evento) => {
                setGratisDesde(evento.target.value);
                setGuardada(null);
              }}
            />
          </div>
          <p className="campo-ayuda">
            Sobre {pesos(Number(gratisDesde) || 0)} no se cobra despacho a ninguna region. Se
            anuncia en la cabecera, la portada y cada ficha.
          </p>
          <PieTarjeta
            visible={gratisCambio}
            guardando={guardando === 'despacho'}
            guardada={guardada === 'despacho'}
          />
        </form>

        <form
          className={retiroHabilitado ? 'tarjeta-condicion' : 'tarjeta-condicion apagada'}
          onSubmit={alEnviar('retiro', { plazoRetiro })}
        >
          <h2 className="tarjeta-condicion-titulo">
            <Icono nombre="tienda" />
            Retiro en sala de ventas
          </h2>
          <InterruptorGrande
            encendido={retiroHabilitado}
            titulo={retiroHabilitado ? 'Se ofrece retiro' : 'No se ofrece retiro'}
            detalleEncendido="El cliente puede elegir retirar gratis en el checkout."
            detalleApagado={
              despachoHabilitado
                ? 'El checkout solo ofrece despacho.'
                : 'El checkout no ofrece retiro.'
            }
            deshabilitado={guardando === 'retiro'}
            alCambiar={(encendido) => {
              void guardar('retiro', { retiroHabilitado: encendido });
            }}
          />
          <label htmlFor="cfg-plazo" className="tarjeta-condicion-etiqueta">
            Plazo para retirar
          </label>
          <input
            id="cfg-plazo"
            className={plazoCambio ? 'entrada cambiado' : 'entrada'}
            type="text"
            maxLength={80}
            value={plazoRetiro}
            disabled={!retiroHabilitado}
            onChange={(evento) => {
              setPlazoRetiro(evento.target.value);
              setGuardada(null);
            }}
          />
          <p className="campo-ayuda">Se muestra junto a la opcion de retiro en el checkout.</p>
          <PieTarjeta
            visible={plazoCambio}
            guardando={guardando === 'retiro'}
            guardada={guardada === 'retiro'}
          />
        </form>
      </div>

      {!despachoHabilitado && !retiroHabilitado ? (
        <p className="aviso-despacho" role="status">
          <Icono nombre="alerta" />
          Sin despacho ni retiro, el comprador paga solo los productos y la entrega se coordina por
          telefono: el checkout le muestra el {SITIO.whatsappVisible}.
        </p>
      ) : null}
    </>
  );
}

interface PieTarjetaProps {
  visible: boolean;
  guardando: boolean;
  guardada: boolean;
}

/** El boton aparece solo con algo que guardar; despues, la confirmacion. */
function PieTarjeta({ visible, guardando, guardada }: PieTarjetaProps) {
  if (!visible && !guardada) return null;

  return (
    <div className="admin-acciones">
      {visible ? (
        <button type="submit" className="btn btn-primario btn-sm" disabled={guardando}>
          {guardando ? 'Guardando…' : 'Guardar'}
        </button>
      ) : (
        <span className="etiqueta-ok" role="status">
          <Icono nombre="check" />
          Guardado
        </span>
      )}
    </div>
  );
}
